import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import WarningIcon from '@mui/icons-material/Warning';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  CustomerComment,
  parseCustomerComments,
  serializeCustomerComments,
  addComment,
  updateComment,
  deleteComment,
} from '../services/customerCommentService';
import { getCollectionColors } from '../utils/collectionColors';
import { useConfig } from '../config/env';

interface CommentEditorProps {
  value: string; // JSON string from customerComment
  onChange: (json: string) => void;
  onSave?: (json: string) => Promise<void>; // Optional callback to save to server with updated JSON
  currentUserEmail: string;
  currentUserName?: string; // User's display name for showing in comments
  collectionId: number; // Collection ID where the comment is being added
  collections?: Array<{ id: number; name: string; color?: string }>; // Collections for color lookup
  readOnly?: boolean; // If true, disable adding/editing/deleting comments
  showAllComments?: boolean; // If true, show all comments from all collections (read-only mode)
}

const CommentEditor: React.FC<CommentEditorProps> = ({ value, onChange, onSave, currentUserEmail, currentUserName, collectionId, collections = [], readOnly = false, showAllComments = false }) => {
  const cfg = useConfig();
  const defaultCollectionId = Number(cfg?.defaultCollectionId ?? 0);
  const [allComments, setAllComments] = useState<CustomerComment[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [newCommentText, setNewCommentText] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; commentId: string | null }>({
    open: false,
    commentId: null,
  });
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; commentId: string } | null>(null);
  const skipNextUpdateRef = useRef(false);

  // Parse JSON when value changes from external source
  useEffect(() => {
    // Skip ONLY if this was triggered by our own onChange in the SAME render cycle
    if (skipNextUpdateRef.current) {
      skipNextUpdateRef.current = false;
      return;
    }

    const data = parseCustomerComments(value);
    setAllComments(data.comments);
  }, [value]);

  // Filter comments for display: show all when showAllComments is true, otherwise show only those from current collectionId or without originatorCollection (backward compatibility)
  const comments = showAllComments
    ? allComments // Show all comments when in showAllComments mode
    : allComments.filter(
        (comment) => comment.originatorCollection === undefined || comment.originatorCollection === collectionId,
      );

  // Helper to update comments and notify parent (updates all comments, not just filtered ones)
  const updateCommentsAndNotify = (updatedFilteredComments: CustomerComment[]) => {
    // Merge filtered comments with comments from other collections
    const otherCollectionComments = allComments.filter(
      (comment) => comment.originatorCollection !== undefined && comment.originatorCollection !== collectionId,
    );
    const mergedComments = [...otherCollectionComments, ...updatedFilteredComments];
    setAllComments(mergedComments);
    const json = serializeCustomerComments({ comments: mergedComments });
    skipNextUpdateRef.current = true; // Skip the next update from our own onChange
    onChange(json);
  };

  const handleStartEdit = (comment: CustomerComment) => {
    setEditingId(comment.id);
    setEditText(comment.comment);
    setEditEmail(comment.email);
  };

  const handleSaveEdit = () => {
    if (editingId) {
      const data = { comments: allComments };
      const updated = updateComment(data, editingId, {
        comment: editText,
        email: editEmail,
      });
      // Update all comments and filter for display
      setAllComments(updated.comments);
      const filteredUpdated = updated.comments.filter(
        (comment) => comment.originatorCollection === undefined || comment.originatorCollection === collectionId,
      );
      updateCommentsAndNotify(filteredUpdated);
      setEditingId(null);
      setEditText('');
      setEditEmail('');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
    setEditEmail('');
  };

  const handleDeleteClick = (id: string) => {
    setDeleteConfirm({ open: true, commentId: id });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.commentId) {
      const data = { comments: allComments };
      const updated = deleteComment(data, deleteConfirm.commentId!);
      // Update all comments and filter for display
      setAllComments(updated.comments);
      const filteredUpdated = updated.comments.filter(
        (comment) => comment.originatorCollection === undefined || comment.originatorCollection === collectionId,
      );
      updateCommentsAndNotify(filteredUpdated);
    }
    setDeleteConfirm({ open: false, commentId: null });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ open: false, commentId: null });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, commentId: string) => {
    setMenuAnchor({ element: event.currentTarget, commentId });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleAddComment = async () => {
    if (newCommentText.trim() && !readOnly) {
      const data = { comments: allComments };
      const updated = addComment(data, newCommentText, currentUserEmail, new Date().toISOString(), collectionId, currentUserName);
      // Update all comments and filter for display
      setAllComments(updated.comments);
      const filteredUpdated = updated.comments.filter(
        (comment) => comment.originatorCollection === undefined || comment.originatorCollection === collectionId,
      );
      
      // Merge filtered comments with comments from other collections to get the full JSON
      const otherCollectionComments = updated.comments.filter(
        (comment) => comment.originatorCollection !== undefined && comment.originatorCollection !== collectionId,
      );
      const mergedComments = [...otherCollectionComments, ...filteredUpdated];
      const json = serializeCustomerComments({ comments: mergedComments });
      
      updateCommentsAndNotify(filteredUpdated);
      setNewCommentText('');
      
      // Save to server if onSave callback is provided
      if (onSave) {
        try {
          await onSave(json);
        } catch (error) {
          console.error('Error saving comment:', error);
        }
      }
    }
  };

  const formatDateTime = (datetime: string): string => {
    try {
      const d = new Date(datetime);
      if (!isNaN(d.valueOf())) {
        return new Intl.DateTimeFormat('no-NO', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: 'Europe/Oslo',
        }).format(d);
      }
    } catch {
      // fall through
    }
    return datetime;
  };

  return (
    <Box>
      {comments.map((comment) => (
        <Box key={comment.id} sx={{ mb: 2 }}>
            {editingId === comment.id ? (
              // Edit mode (should not be accessible when readOnly, but prevent saving if somehow triggered)
              <Box>
                <TextField fullWidth label="E-post" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} size="small" sx={{ mb: 2 }} disabled={readOnly} />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Kommentar"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  size="small"
                  sx={{ mb: 2 }}
                  disabled={readOnly}
                />
                {!readOnly && (
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <IconButton onClick={handleCancelEdit} size="small" color="error">
                      <CancelIcon />
                    </IconButton>
                    <IconButton onClick={handleSaveEdit} size="small" color="primary">
                      <SaveIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ) : (
              // Display mode
              <Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                  {(() => {
                    let colors = { bg: '#e0e0e0', color: '#000' }; // Default gray

                    // First try to find in collections array
                    const originator = collections.find((c) => c.id === comment.originatorCollection);
                    if (originator) {
                      colors = getCollectionColors({ id: originator.id, name: originator.name, color: originator.color });
                    } else if (comment.originatorCollection !== undefined) {
                      // Handle special collections when collections array is not provided
                      switch (comment.originatorCollection) {
                        case 0: // Favoritter
                          colors = { bg: '#f44336', color: '#000' }; // Red like favorite heart
                          break;
                        case 1: // AF Gruppen
                          colors = { bg: '#FFEB3B', color: '#000' }; // Yellow
                          break;
                        default:
                          // Check if it's the default collection (Byggforsk samling)
                          if (comment.originatorCollection === defaultCollectionId) {
                            colors = { bg: '#0d47a1', color: '#000' }; // Dark blue
                          }
                          break;
                      }
                    }

                    const displayName = comment.name || comment.email || 'Anonym';
                    return (
                      <Avatar sx={{ bgcolor: colors.bg, width: 32, height: 32 }}>
                        <Typography variant="caption" sx={{ color: colors.color, fontWeight: 'bold' }}>
                          {displayName.charAt(0).toUpperCase()}
                        </Typography>
                      </Avatar>
                    );
                  })()}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.primary" sx={{ display: 'inline' }}>
                      {comment.name || comment.email || 'Anonym'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'inline', ml: 1 }}>
                      {formatDateTime(comment.datetime)}
                    </Typography>
                  </Box>
                  {!readOnly && (
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, comment.id)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                  {comment.comment}
                </Typography>
              </Box>
            )}
        </Box>
      ))}

      {/* Edit/Delete Menu */}
      <Menu
        anchorEl={menuAnchor?.element}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}>
        <MenuItem
          onClick={() => {
            if (menuAnchor) {
              const comment = comments.find((c) => c.id === menuAnchor.commentId);
              if (comment) {
                handleStartEdit(comment);
              }
            }
            handleMenuClose();
          }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Rediger
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAnchor) {
              handleDeleteClick(menuAnchor.commentId);
            }
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Slett
        </MenuItem>
      </Menu>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Ny kommentar
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={newCommentText}
          onChange={(e) => setNewCommentText(e.target.value)}
          placeholder="It is a long established fact that a reader will be distracted by the readable content of a page when looking at its layout."
          sx={{ mb: 2 }}
          disabled={readOnly}
        />
        {!readOnly && (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button onClick={() => setNewCommentText('')} size="small" sx={{ color: 'text.secondary', textTransform: 'none' }}>
              Avbryt
            </Button>
            <IconButton onClick={handleAddComment} size="small" sx={{ color: 'text.secondary' }} disabled={!newCommentText.trim()}>
              <SendIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirm.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description">
        <DialogTitle id="delete-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Bekreft sletting
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Er du sikker på at du vil slette denne kommentaren? Denne handlingen kan ikke angres.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="inherit">
            Avbryt
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" startIcon={<DeleteIcon />}>
            Slett
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommentEditor;
